/**
 * Privacy Policy Page
 *
 * Consumer Health Data Privacy Policy for Hesvara.
 * Standalone page accessible to both authenticated and unauthenticated users.
 */

import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function PrivacyPolicy(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-airy">
      {/* Navigation Header */}
      <div className="bg-white border-b border-soft-neutral">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="btn-ghost flex items-center gap-2 -ml-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <article className="prose-hesvara">
          {/* Header */}
          <header className="text-center mb-10">
            <h1 className="font-display text-4xl text-deep-forest font-light mb-2">
              Hesvara Privacy Statement
            </h1>
            <p className="text-evergreen text-base mb-4">
              Consumer Health Data Privacy Policy
            </p>
            <p className="text-muted text-sm italic">
              Effective Date: January 2026
            </p>
          </header>

          {/* Beta Notice */}
          <div className="border border-brass rounded-md p-4 mb-8 bg-airy">
            <p className="text-sm m-0">
              <strong className="text-brass-text-warm">BETA NOTICE: </strong>
              Hesvara is currently in beta. We are actively refining our platform and security
              measures based on user feedback. This privacy statement will be updated as we
              approach general availability. We will notify you of any material changes.
            </p>
          </div>

          {/* Our Commitment */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Our Commitment to You</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We understand you're entrusting Hesvara with sensitive information about your
              loved one's care. That trust is not something we take lightly. This statement
              explains how we protect your data and respect your privacy—written in plain
              language, not legal jargon.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Hesvara is built with healthcare-grade security standards. We implement technical
              and administrative safeguards comparable to those required of healthcare providers,
              because your health information deserves that level of protection.
            </p>
          </section>

          {/* What We Collect */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">What Information We Collect</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Hesvara collects only the information necessary to help you organize care and
              advocate for your loved one. <strong>All consumer health data is collected directly
              from you through your use of the Hesvara platform. We do not collect health data
              from third-party sources.</strong>
            </p>

            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Account Information:</strong> Your name, email address, and password to
              create and secure your account.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Care Recipient Information:</strong> Basic details about the person you're
              caring for, such as their name, date of birth, insurance information, and state of
              residence—used to track relevant deadlines and eligibility.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Documents You Upload:</strong> Medical records, insurance paperwork, school
              documents, and other files you choose to store in your CaseFile.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Daily Records:</strong> Notes, observations, and logs you create about daily
              care activities, symptoms, or incidents.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Generated Documents:</strong> Letters, appeals, and other advocacy documents
              created through CaseMaker based on your information.
            </p>
          </section>

          {/* How We Use */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">How We Use Your Information</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We use your information <strong>solely</strong> to provide the Hesvara service:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li className="text-ink text-[15px]">Organizing and making your documents searchable</li>
              <li className="text-ink text-[15px]">Monitoring deadlines, transitions, and eligibility requirements</li>
              <li className="text-ink text-[15px]">Generating advocacy documents tailored to your situation</li>
              <li className="text-ink text-[15px]">Identifying patterns or trends in your daily records that may require attention</li>
              <li className="text-ink text-[15px]">Sending you alerts and reminders about upcoming deadlines</li>
            </ul>
            <p className="text-ink text-[15px] mb-3 leading-relaxed font-semibold">
              We do not sell your data. We do not use your information for advertising or marketing.
              We do not share your data with third parties except as described below.
            </p>
          </section>

          {/* How We Protect */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">How We Protect Your Information</h2>
            <p className="text-ink text-[15px] mb-4 leading-relaxed">
              We implement healthcare-grade security measures:
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">Encryption</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Your data is encrypted both when it travels to our servers (in transit) and when
              it's stored (at rest). Even if data were somehow accessed, it would be unreadable
              without the encryption keys.
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">Access Controls</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Only you (and any caregivers you authorize) can access your CaseFile. Our team
              cannot view your personal health information unless you specifically request
              support that requires it—and even then, access is logged and limited.
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">Authentication</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We require strong passwords and encourage two-factor authentication to protect
              your account from unauthorized access.
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">Audit Logging</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We maintain detailed logs of who accesses data and when, allowing us to detect
              and investigate any suspicious activity.
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">Data Backup & Recovery</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Your CaseFile is regularly backed up to secure, encrypted storage. We have
              disaster recovery procedures in place to ensure your data isn't lost.
            </p>

            <h3 className="text-base font-semibold text-ink mt-5 mb-2">U.S.-Based Infrastructure</h3>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              All data is stored on servers located in the United States, within data centers
              that meet healthcare-grade compliance standards.
            </p>
          </section>

          {/* Consent & Authorization */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Consent & Authorization</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Because Hesvara handles sensitive health information, we take consent seriously:
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>If you are the patient:</strong> You control your own data. You decide
              what to upload and who can access it.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>If you are caring for a minor child or legal dependent:</strong> As their
              parent or legal guardian, you have authority to manage their health information.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>If you are caring for an adult:</strong> You should have the patient's
              consent to use Hesvara on their behalf. During onboarding, we'll ask you to
              confirm you have authorization to manage their care information. If you have
              healthcare power of attorney or legal guardianship, you may act as their
              personal representative.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              The patient (or their authorized representative) can revoke consent and request
              data deletion at any time.
            </p>
          </section>

          {/* Minimum Necessary */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">We Only Use What's Needed</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Following the principle of data minimization, our AI features only access and
              include information relevant to the specific task. For example, when CaseMaker
              drafts an appeal letter, it includes only the medical details necessary to
              support that specific request—not your entire health history.
            </p>
          </section>

          {/* Sharing */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">When We Share Information</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We share your information only in these limited circumstances:
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>At Your Direction:</strong> When you export documents, download letters,
              or explicitly share information with healthcare providers, schools, or insurers.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>With Authorized Caregivers:</strong> If you invite additional family
              members or caregivers to access a CaseFile, they will see the information
              you've authorized them to view.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Service Providers:</strong> We use the following categories of service
              providers: (1) cloud infrastructure for secure data storage, and (2) AI processing
              services for document analysis and generation. Each provider is selected for their
              compliance with healthcare-grade security standards and is bound by data protection
              agreements requiring equivalent security controls. We do not share your health data
              with any other categories of third parties.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Legal Requirements:</strong> If required by law, we may disclose
              information—but we will notify you unless legally prohibited from doing so.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Your Rights</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              You have control over your information:
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Access:</strong> You can view and download all information in your
              CaseFile at any time.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Correction:</strong> You can update or correct any information you've entered.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Withdraw Consent:</strong> You may withdraw consent to future collection
              of your health data at any time while retaining access to previously stored data.
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Deletion:</strong> You can request full deletion of your account and all
              associated data. We will securely erase your information, though some may be
              retained as required by law or for legitimate business purposes (such as fraud prevention).
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Revoke Caregiver Access:</strong> If a caregiver relationship changes,
              you can remove their access to your CaseFile at any time.
            </p>
          </section>

          {/* Breach */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">If Something Goes Wrong</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              While we work hard to prevent security incidents, we have procedures in place
              if a breach occurs. If we discover unauthorized access to your health data, we
              will notify affected users within 60 days of discovering the breach, investigate
              the incident, and take steps to prevent future occurrences. Our commitment is
              transparency: you will know if your information has been compromised.
            </p>
          </section>

          {/* Beta-Specific */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Beta-Specific Considerations</h2>
            <div className="border border-brass rounded-md p-4 bg-airy">
              <p className="text-sm mb-3">
                <strong className="text-brass-text-warm">The following items are specific to our beta period and may change:</strong>
              </p>
              <ul className="list-disc pl-6 mb-3 space-y-2">
                <li className="text-ink text-[15px]">Security features are being continuously enhanced based on testing and feedback.</li>
                <li className="text-ink text-[15px]">Some features may be modified or removed as we refine the platform.</li>
                <li className="text-ink text-[15px]">We may reach out to beta users for feedback on privacy and security experiences.</li>
                <li className="text-ink text-[15px]">Data retention policies will be finalized before general availability.</li>
                <li className="text-ink text-[15px]">Additional third-party integrations may be added, each with appropriate privacy safeguards.</li>
              </ul>
              <p className="text-muted text-sm italic m-0">
                We will notify beta users of any material changes to this privacy statement before they take effect.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Questions or Concerns</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              Your privacy matters to us. If you have questions about this statement or how
              we handle your information, please contact us:
            </p>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              <strong>Email:</strong> privacy@hesvara.com
            </p>
          </section>

          {/* Updates */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-evergreen mb-4">Updates to This Statement</h2>
            <p className="text-ink text-[15px] mb-3 leading-relaxed">
              We may update this privacy statement as our service evolves or as regulations
              change. We will post any changes here with an updated effective date. For
              significant changes, we will notify you directly via email or within the application.
            </p>
          </section>

          {/* Footer */}
          <footer className="text-center mt-12 pt-6 border-t border-soft-neutral">
            <p className="text-muted mb-4">—</p>
            <p className="text-evergreen text-[15px] italic">Your Secure Casefile and Trusted Administrative Assistant</p>
          </footer>
        </article>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
